import React, { Component, ReactNode } from 'react';
import { ui } from '../utils';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorId?: string;
  // D6-15: bumping this key forces a full re-mount of the child tree on
  // "Try again" — plain setState({ hasError: false }) would re-render the
  // same broken tree and immediately re-throw.
  remountKey: number;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, remountKey: 0 };

  // D6-15: key-based re-mount retry. Recovers from transient render crashes
  // (bad payload already gone from the store, race conditions) without losing
  // the whole SPA state the way window.location.reload() does.
  handleRetry = () => {
    this.setState((previous) => ({
      hasError: false,
      error: undefined,
      errorId: undefined,
      remountKey: previous.remountKey + 1,
    }));
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo);

    void fetch('/api/client-errors', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        url: window.location.href,
        userAgent: window.navigator.userAgent,
        timestamp: new Date().toISOString(),
      }),
      keepalive: true,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((body: { errorId?: unknown } | null) => {
        if (body && typeof body.errorId === 'string' && body.errorId) {
          this.setState({ errorId: body.errorId });
        }
      })
      .catch((reportError) => {
        console.warn('Failed to report client error:', reportError);
      });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full items-center justify-center bg-background px-6 text-foreground">
          <div className="w-full max-w-md rounded-[2rem] border border-border bg-card px-8 py-10 text-center shadow-2xl shadow-black/10">
            <h1 className="mb-4 text-2xl font-bold text-foreground-bright">Ally ran into a problem</h1>
            {/* D1-09: never surface raw error messages or stack traces to end
                users — they can contain internals like ECONNREFUSED or
                component trees. Show a generic message plus a support
                reference code; full details are logged server-side. */}
            <p className="mb-6 text-muted-foreground">
              Something unexpected went wrong. Please reload the application — if the problem keeps
              happening, contact support.
            </p>
            {this.state.errorId ? (
              <p className="mb-6 text-xs text-muted-foreground">
                Reference code: <span className="font-mono text-foreground">{this.state.errorId}</span>
              </p>
            ) : null}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleRetry}
                className={`${ui.button.base} ${ui.button.primary} px-4 py-2.5`}
              >
                Try again
              </button>
              <button
                onClick={() => window.location.reload()}
                className={`${ui.button.base} ${ui.button.secondary} px-4 py-2.5`}
              >
                Reload Application
              </button>
            </div>
            {import.meta.env.DEV ? (
              <div className="mt-4">
                <details className="text-left">
                  <summary className="cursor-pointer text-muted-foreground transition-colors duration-150 hover:text-foreground">
                    Technical Details (dev only)
                  </summary>
                  <pre className="mt-2 max-h-32 overflow-auto rounded-xl border border-border bg-background p-3 text-xs text-muted-foreground">
                    {this.state.error?.message}
                    {'\n'}
                    {this.state.error?.stack || 'No stack trace available'}
                  </pre>
                </details>
              </div>
            ) : null}
          </div>
        </div>
      );
    }

    return <React.Fragment key={this.state.remountKey}>{this.props.children}</React.Fragment>;
  }
}

export default ErrorBoundary;
